import './QuickBookmarks.css';

import { IonIcon } from '@ionic/react';
import { checkmarkOutline, closeOutline } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import Button from '../Button/Button';
import { useQuickBookmarks } from '../../context/QuickBookmarksContext/QuickBookmarksContext';
import { useTranslate } from '../../hooks/context/useTranslate';
import { QuickBookmarkKind } from '../../types/quickBookmarks';
import DateTimeTag from './DateTimeTag';

const formatDateTime = (value: string | null): string =>
  value ? new Date(value).toLocaleString() : '---';

const QuickBookmarksPanel = () => {
  const { t } = useTranslate();
  const {
    items,
    view,
    draftNavigation,
    loading,
    saving,
    error,
    close,
    create,
    update,
    show,
    reload,
  } = useQuickBookmarks();
  const [kind, setKind] = useState<QuickBookmarkKind | null>(null);
  const [note, setNote] = useState('');
  const [reminderAt, setReminderAt] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (view !== 'create') return;
    setKind(null);
    setNote('');
    setReminderAt(null);
  }, [view]);

  useEffect(() => {
    if (!kind) return;
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  }, [kind]);

  if (view === 'closed') return null;

  return (
    <aside
      className="quick-bookmarks-panel"
      role="complementary"
      aria-label={t('quickBookmarks.title')}
      data-quick-bookmarks-panel
    >
      <header className="quick-bookmarks-head">
        <div>
          <div className="quick-bookmarks-eyebrow">
            {t('quickBookmarks.title')}
          </div>
          <h2>
            {view === 'create'
              ? t('quickBookmarks.new')
              : t('quickBookmarks.list')}
          </h2>
        </div>
        <button
          type="button"
          className="quick-bookmarks-close"
          onClick={close}
          aria-label={t('quickBookmarks.close')}
        >
          <IonIcon icon={closeOutline} />
        </button>
      </header>

      <div className="quick-bookmarks-body">
        {view === 'create' ? (
          <form
            className="quick-bookmark-paper quick-bookmark-editor"
            onSubmit={(event) => {
              event.preventDefault();
              if (!kind || !note.trim()) return;
              void create({
                kind,
                note,
                navigation: kind === 'navigation' ? draftNavigation : null,
                reminderAt,
              });
            }}
          >
            <div className="quick-bookmark-kind-row">
              <button
                type="button"
                className={`quick-bookmark-tag${kind === 'navigation' ? ' is-active' : ''}`}
                disabled={!draftNavigation}
                onClick={() => setKind('navigation')}
              >
                {t('quickBookmarks.withNavigation')}
              </button>
              <button
                type="button"
                className={`quick-bookmark-tag${kind === 'free' ? ' is-active' : ''}`}
                onClick={() => setKind('free')}
              >
                {t('quickBookmarks.free')}
              </button>
            </div>

            {kind === 'navigation' && draftNavigation && (
              <div className="quick-bookmark-path">
                {draftNavigation.displayPath.join(' › ')}
              </div>
            )}

            <label className="quick-bookmark-note-label">
              <span>{t('quickBookmarks.note')}</span>
              <textarea
                ref={textareaRef}
                value={note}
                maxLength={2000}
                rows={6}
                disabled={!kind || saving}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>

            <div className="quick-bookmark-reminder-row">
              <DateTimeTag
                value={reminderAt}
                label={t('quickBookmarks.reminder')}
                onChange={setReminderAt}
                disabled={saving}
              />
              <span>{formatDateTime(reminderAt)}</span>
            </div>

            {error && (
              <div className="quick-bookmarks-error">
                {t('quickBookmarks.saveError')}
              </div>
            )}

            <div className="quick-bookmark-editor-actions">
              <Button isSubmit disabled={!kind || !note.trim() || saving}>
                {t('quickBookmarks.save')}
              </Button>
              <Button color="light" fill="solid" onClick={close}>
                {t('quickBookmarks.discard')}
              </Button>
            </div>
          </form>
        ) : (
          <>
            {loading && <p>{t('quickBookmarks.loading')}</p>}
            {error && (
              <div className="quick-bookmarks-error">
                {t('quickBookmarks.loadError')}{' '}
                <button type="button" onClick={reload}>
                  {t('quickBookmarks.retry')}
                </button>
              </div>
            )}
            {!loading && !error && items.length === 0 && (
              <p className="quick-bookmarks-empty">
                {t('quickBookmarks.empty')}
              </p>
            )}
            <div className="quick-bookmarks-list">
              {items.map((item) => {
                const overdue =
                  item.reminderAt !== null &&
                  Date.parse(item.reminderAt) <= Date.now();
                return (
                  <article
                    key={item.id}
                    className={`quick-bookmark-paper${overdue ? ' is-overdue' : ''}`}
                  >
                    {item.navigation && (
                      <div className="quick-bookmark-path">
                        {item.navigation.displayPath.join(' › ')}
                      </div>
                    )}
                    <p className="quick-bookmark-note">{item.note}</p>
                    <div className="quick-bookmark-meta">
                      <span>{t('quickBookmarks.reminder')}</span>
                      <strong>{formatDateTime(item.reminderAt)}</strong>
                    </div>
                    <footer className="quick-bookmark-actions">
                      {item.navigation && (
                        <button type="button" onClick={() => show(item)}>
                          {t('quickBookmarks.show')}
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() =>
                          void update(item.id, { status: 'completed' })
                        }
                      >
                        <IonIcon icon={checkmarkOutline} />
                        {t('quickBookmarks.completed')}
                      </button>
                      <DateTimeTag
                        value={item.reminderAt}
                        label={t('quickBookmarks.postpone')}
                        disabled={saving}
                        onChange={(value) =>
                          void update(item.id, { reminderAt: value })
                        }
                      />
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() =>
                          void update(item.id, { status: 'cancelled' })
                        }
                      >
                        {t('quickBookmarks.cancel')}
                      </button>
                    </footer>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </aside>
  );
};

export default QuickBookmarksPanel;
